const kabookiCoreScript = document.getElementById('js-kabooki-core')
const kabookiAssetBase = kabookiCoreScript
  ? kabookiCoreScript.dataset.assetBase
  : ''
const kabookiAssetVersion = kabookiCoreScript
  ? kabookiCoreScript.dataset.assetVersion
  : ''
const kabookiFeatureBundles = new Map()
const kabookiLoadedBundles = new Set()

function loadKabookiFeatureBundle(name) {
  // All Vue consumers share one runtime, even when search and product loading overlap.
  if (name === 'vue-interfaces' && !kabookiLoadedBundles.has('vue-runtime')) {
    return loadKabookiFeatureBundle('vue-runtime')
      .then(() => loadKabookiFeatureBundle(name))
  }
  if (!kabookiAssetBase) return Promise.reject(new Error('Asset base missing.'))
  if (kabookiFeatureBundles.has(name)) return kabookiFeatureBundles.get(name)

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `${kabookiAssetBase}${name}.min.js?version=${kabookiAssetVersion}`
    script.async = true
    script.dataset.cookieconsent = 'ignore'
    script.onload = () => {
      kabookiLoadedBundles.add(name)
      resolve()
    }
    script.onerror = (error) => {
      kabookiFeatureBundles.delete(name)
      reject(error)
    }
    document.head.append(script)
  })

  kabookiFeatureBundles.set(name, promise)
  return promise
}

function loadKabookiBundleNearElements(name, elements, rootMargin = '600px') {
  if (!elements.length) return

  if (!('IntersectionObserver' in window)) {
    loadKabookiFeatureBundle(name).catch(() => {})
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      loadKabookiFeatureBundle(name).catch(() => {})
    },
    { rootMargin }
  )

  elements.forEach((element) => observer.observe(element))
}

function loadKabookiVueFeatures() {
  return loadKabookiFeatureBundle('vue-interfaces')
}

function initialisePageBuilderLazySections() {
  const sections = Array.from(
    document.querySelectorAll('[data-page-builder-lazy-section]')
  )
  if (!sections.length) return

  const loadSection = (section) => {
    if (section.dataset.pageBuilderLoading === 'true') return

    const sectionKey = section.dataset.pageBuilderSectionKey
    if (!sectionKey) return

    section.dataset.pageBuilderLoading = 'true'
    section.setAttribute('aria-busy', 'true')
    section.classList.remove('products--lazy-error')
    const previousError = section.querySelector('.products-lazy-skeleton__error')
    if (previousError) previousError.hidden = true

    const url = new URL(window.location.href)
    url.searchParams.set('kabooki_lazy_section', sectionKey)

    fetch(url.href, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load Page Builder section.')
        return response.text()
      })
      .then((html) => {
        const responsePage = new DOMParser().parseFromString(html, 'text/html')
        const replacement = Array.from(
          responsePage.querySelectorAll('[data-page-builder-section-key]')
        ).find(
          (candidate) => candidate.dataset.pageBuilderSectionKey === sectionKey
        )

        if (!replacement) throw new Error('Page Builder section is missing.')

        section.replaceWith(replacement)
        loadKabookiFeatureBundle('products').catch(() => {})
        document.dispatchEvent(
          new CustomEvent('kabooki:page-builder-section-loaded', {
            detail: { element: replacement, key: sectionKey },
          })
        )
      })
      .catch(() => {
        section.dataset.pageBuilderLoading = 'false'
        section.setAttribute('aria-busy', 'false')
        section.classList.add('products--lazy-error')
        const error = section.querySelector('.products-lazy-skeleton__error')
        if (error) error.hidden = false
      })
  }

  sections.forEach((section) => {
    const retry = section.querySelector('[data-page-builder-lazy-retry]')
    if (retry) retry.addEventListener('click', () => loadSection(section))
  })

  if (!('IntersectionObserver' in window)) {
    sections.forEach(loadSection)
    return
  }

  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection
  const rootMargin = connection && connection.saveData ? '200px' : '900px'
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        observer.unobserve(entry.target)
        loadSection(entry.target)
      })
    },
    { rootMargin }
  )

  sections.forEach((section) => observer.observe(section))
}

initialisePageBuilderLazySections()

const kabookiLayeredImageLogos = Array.from(
  document.querySelectorAll('.layered-image-cta__logo')
)

if (kabookiLayeredImageLogos.length) {
  kabookiLayeredImageLogos.forEach((logo) => {
    logo.classList.add('layered-image-cta__logo--reveal-ready')
  })

  if (!('IntersectionObserver' in window)) {
    kabookiLayeredImageLogos.forEach((logo) => {
      logo.classList.add('layered-image-cta__logo--revealed')
    })
  } else {
    const layeredImageLogoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return

          entry.target.classList.add('layered-image-cta__logo--revealed')
          layeredImageLogoObserver.unobserve(entry.target)
        })
      },
      { threshold: 0.35 }
    )

    kabookiLayeredImageLogos.forEach((logo) => {
      layeredImageLogoObserver.observe(logo)
    })
  }
}

const kabookiHeroSlideshows = Array.from(
  document.querySelectorAll('.js-hero-slideshow')
)
if (
  kabookiHeroSlideshows.some(
    (slideshow) => slideshow.querySelectorAll('.hero-media__slide').length > 1
  )
) {
  // A synthetic or passive page scroll must not start the slideshow during the
  // initial rendering path. Pointer and keyboard input are deliberate user
  // interactions and still make the slideshow available immediately.
  const heroInteractionEvents = ['pointerdown', 'keydown']
  const loadHeroAfterInteraction = () => {
    heroInteractionEvents.forEach((eventName) => {
      window.removeEventListener(eventName, loadHeroAfterInteraction)
    })
    loadKabookiFeatureBundle('hero').catch(() => {})
  }

  heroInteractionEvents.forEach((eventName) => {
    window.addEventListener(eventName, loadHeroAfterInteraction, {
      passive: true,
      once: true,
    })
  })
}

const productInterfaces = Array.from(
  document.querySelectorAll(
    '.products, .inner-product, .js-product-colour-variants, .product-page__ai-generated'
  )
)
loadKabookiBundleNearElements('products', productInterfaces, '250px')

const kabookiVueInterfaces = Array.from(
  document.querySelectorAll('#js-ajax-list, #js-stores')
)
loadKabookiBundleNearElements('vue-interfaces', kabookiVueInterfaces, '300px')

document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest('.js-search-trigger')
    if (
      !trigger ||
      (kabookiLoadedBundles.has('vue-interfaces') &&
        kabookiLoadedBundles.has('products'))
    ) {
      return
    }
    event.preventDefault()
    event.stopImmediatePropagation()
    Promise.all([
      loadKabookiVueFeatures(),
      loadKabookiFeatureBundle('products'),
    ])
      .then(() => {
        trigger.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true })
        )
      })
      .catch(() => {})
  },
  true
)

// A quick-add click must never be lost while its feature bundle is loading.
document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest('.js-product-quick-add')
    if (!trigger || kabookiLoadedBundles.has('products')) return
    event.preventDefault()
    event.stopImmediatePropagation()
    loadKabookiFeatureBundle('products')
      .then(() => trigger.click())
      .catch(() => {})
  },
  true
)

if (
  document.body.classList.contains('product-page-body') ||
  document.querySelector('.js-add-to-basket-form') ||
  document.querySelector('#js-product-slider')
) {
  const loadProductPage = () =>
    loadKabookiFeatureBundle('vue-runtime')
      .then(() => loadKabookiFeatureBundle('product-page'))
      .catch(() => {})

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(loadProductPage, { timeout: 800 })
  } else {
    window.setTimeout(loadProductPage, 250)
  }
}

if (document.body.classList.contains('checkout')) {
  loadKabookiVueFeatures()
    .then(() => loadKabookiFeatureBundle('checkout'))
    .catch(() => {})
}
